import { Link } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import routes from "routes";
import footerRoutes from "footer.routes";

import bgImage from "assets/images/bg-sign-in-basic.jpg";

const enrollmentSteps = [
  {
    step: 1,
    title: "Inquiry",
    description: "Contact us to learn more about our school, arrange a visit, or request information.",
    icon: "help_outline",
  },
  {
    step: 2,
    title: "School Visit",
    description: "Schedule a tour of our campus to see our facilities and meet our staff.",
    icon: "visibility",
  },
  {
    step: 3,
    title: "Application",
    description: "Complete and submit the application form with all required documents.",
    icon: "description",
  },
  {
    step: 4,
    title: "Assessment",
    description: "Your child may be assessed to determine the appropriate grade level placement.",
    icon: "assignment",
  },
  {
    step: 5,
    title: "Admission Decision",
    description: "You will be notified of the admission decision within a few days.",
    icon: "mail",
  },
  {
    step: 6,
    title: "Enrollment",
    description: "Complete enrollment by paying fees and submitting remaining documents.",
    icon: "check_circle",
  },
];

const requirements = [
  "Completed application form",
  "Birth certificate (certified copy)",
  "Recent passport-size photographs (2)",
  "Previous school reports (if applicable)",
  "Immunization records",
  "Proof of residence",
  "Parent/Guardian ID copy",
];

const ageRequirements = [
  { grade: "ECD A", age: "3-4 years" },
  { grade: "ECD B", age: "4-5 years" },
  { grade: "Grade 1", age: "5-6 years" },
  { grade: "Grade 2", age: "6-7 years" },
  { grade: "Grade 3", age: "7-8 years" },
  { grade: "Grade 4", age: "8-9 years" },
  { grade: "Grade 5", age: "9-10 years" },
  { grade: "Grade 6", age: "10-11 years" },
  { grade: "Grade 7", age: "11-12 years" },
];

function Admissions() {
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/contact",
          label: "Contact Us",
          color: "success",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Admissions
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              Join the Rose of Sharon family - Your child&apos;s journey to excellence starts here
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Admission Requirements */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" mb={2}>
                  Admission Requirements
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={3}>
                  To apply for admission to Rose of Sharon Primary School, please prepare 
                  the following documents:
                </MKTypography>
                <MKBox>
                  {requirements.map((req, index) => (
                    <MKBox display="flex" alignItems="center" mb={1.5} key={index}>
                      <Icon sx={{ color: "#006747", mr: 1.5 }}>check_circle</Icon>
                      <MKTypography variant="body1" color="text">
                        {req}
                      </MKTypography>
                    </MKBox>
                  ))}
                </MKBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600"
                  alt="Students"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 400 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Enrollment Process */}
        <MKBox component="section" py={6} sx={{ backgroundColor: "#f8f9fa" }}>
          <Container>
            <Grid container justifyContent="center" sx={{ textAlign: "center", mb: 4 }}>
              <Grid item xs={12} md={8}>
                <MKTypography variant="h3" mb={1}>
                  Enrollment Process
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  Follow these simple steps to enroll your child at Rose of Sharon Primary School.
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              {enrollmentSteps.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.step}>
                  <MKBox
                    p={3}
                    borderRadius="xl"
                    sx={{
                      backgroundColor: "white",
                      boxShadow: ({ boxShadows: { md } }) => md,
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    <MKBox
                      position="absolute"
                      top={-15}
                      left={20}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="2.5rem"
                      height="2.5rem"
                      borderRadius="50%"
                      sx={{ backgroundColor: "#006747" }}
                    >
                      <MKTypography variant="h6" color="white">
                        {item.step}
                      </MKTypography>
                    </MKBox>
                    <MKBox pt={2}>
                      <MKBox display="flex" alignItems="center" mb={1}>
                        <Icon sx={{ color: "#006747", mr: 1 }}>{item.icon}</Icon>
                        <MKTypography variant="h5">{item.title}</MKTypography>
                      </MKBox>
                      <MKTypography variant="body2" color="text">
                        {item.description}
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* Age Requirements */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" mb={2}>
                  Age Requirements
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={3}>
                  Children must meet the following age requirements by the start of the 
                  academic year to be enrolled in each grade level:
                </MKTypography>
                <MKBox
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "lg",
                    overflow: "hidden",
                  }}
                >
                  {ageRequirements.map((item, index) => (
                    <MKBox
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      p={2}
                      sx={{
                        borderBottom: index < ageRequirements.length - 1 ? "1px solid #e0e0e0" : "none",
                        "&:hover": { backgroundColor: "#e8f5e9" },
                      }}
                    >
                      <MKTypography variant="body1" fontWeight="medium">
                        {item.grade}
                      </MKTypography>
                      <MKTypography variant="body1" color="text">
                        {item.age}
                      </MKTypography>
                    </MKBox>
                  ))}
                </MKBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  p={4}
                  borderRadius="xl"
                  sx={{
                    backgroundColor: "#e8f5e9",
                    textAlign: "center",
                  }}
                >
                  <Icon sx={{ color: "#d32f2f", fontSize: "4rem", mb: 2 }}>school</Icon>
                  <MKTypography variant="h4" mb={2}>
                    School Fees
                  </MKTypography>
                  <MKTypography variant="body1" color="text" mb={3}>
                    For detailed information about our school fees structure, please contact 
                    our admissions office or visit the school in person.
                  </MKTypography>
                  <MKButton
                    variant="gradient"
                    color="success"
                    component={Link}
                    to="/contact"
                  >
                    Contact Admissions
                  </MKButton>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Download Application Form */}
        <MKBox
          component="section"
          py={6}
          sx={{
            background: "linear-gradient(135deg, #006747 0%, #004d35 100%)",
            borderRadius: "xl",
            mx: { xs: 1, lg: 3 },
            my: 4,
            textAlign: "center",
          }}
        >
          <Container>
            <Icon sx={{ color: "#d32f2f", fontSize: "4rem", mb: 2 }}>description</Icon>
            <MKTypography variant="h3" color="white" mb={2}>
              Ready to Apply?
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.9} mb={4}>
              Download our application form or visit our school to pick up a copy. 
              Our admissions team is ready to assist you through the process.
            </MKTypography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <MKButton
                  variant="contained"
                  color="white"
                  size="large"
                  sx={{ color: "#006747" }}
                >
                  Download Application Form
                </MKButton>
              </Grid>
              <Grid item>
                <MKButton
                  variant="outlined"
                  color="white"
                  size="large"
                  component={Link}
                  to="/contact"
                >
                  Contact Admissions Office
                </MKButton>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Contact for Admissions */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container justifyContent="center">
              <Grid item xs={12} md={8} textAlign="center">
                <MKTypography variant="h3" mb={2}>
                  Need Help with Admissions?
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={3}>
                  Our friendly admissions team is here to answer your questions and guide 
                  you through the enrollment process. Don&apos;t hesitate to reach out!
                </MKTypography>
                <Grid container spacing={3} justifyContent="center">
                  <Grid item xs={12} sm={4}>
                    <MKBox textAlign="center">
                      <Icon sx={{ color: "#d32f2f", fontSize: "2rem", mb: 1 }}>phone</Icon>
                      <MKTypography variant="h6" mb={0.5}>
                        Phone
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        +263 XX XXX XXXX
                      </MKTypography>
                    </MKBox>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <MKBox textAlign="center">
                      <Icon sx={{ color: "#d32f2f", fontSize: "2rem", mb: 1 }}>email</Icon>
                      <MKTypography variant="h6" mb={0.5}>
                        Email
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        admissions@roseofsharonschool.com
                      </MKTypography>
                    </MKBox>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <MKBox textAlign="center">
                      <Icon sx={{ color: "#d32f2f", fontSize: "2rem", mb: 1 }}>schedule</Icon>
                      <MKTypography variant="h6" mb={0.5}>
                        Office Hours
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        Mon - Fri: 8:00 AM - 4:00 PM
                      </MKTypography>
                    </MKBox>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Admissions;
